import React from 'react';
import { Skeleton } from '@mui/material';
import pagesStandardStyles from '../../../../pages/Pages.module.css';
import skeletonStyles from './PerfOrderDetailsSkeletonDataGrid.module.css';

const SKELETON_ROW_HEIGHT = 52;
const COLUMN_MIN_WIDTH = 115.2;

interface PerfOrderDetailsSkeletonDataGridProps {
  /**
   * Whether the layout is mobile (hides Take Action column)
   * @default false
   */
  isMobile?: boolean;
}

const renderRow = (rowIndex: number, columnCount: number): JSX.Element => (
  <div
    key={rowIndex}
    className={skeletonStyles.skeletonRow}>
    {Array.from({ length: columnCount }).map((_, colIndex) => (
      <Skeleton
        key={`row-${rowIndex}-col-${colIndex}`}
        variant="rectangular"
        height={SKELETON_ROW_HEIGHT}
        sx={{
          flex: 1,
          minWidth: COLUMN_MIN_WIDTH,
          marginRight: colIndex < columnCount - 1 ? '1px' : 0,
          bgcolor: rowIndex % 2 === 0 ? 'background.paper' : 'grey.50',
          ...(colIndex === 0 && { borderLeft: 1, borderColor: 'divider' }),
        }}
      />
    ))}
  </div>
);

/**
 * PerfOrderDetailsSkeletonDataGrid Component - Displays loading skeleton for Order Details DataGrid.
 * Matches the exact structure of the Order Details grid: 2 data rows, 3-4 columns depending on viewport.
 * Columns: Status | More Info | Take Action (desktop only) | Quantity
 */
const PerfOrderDetailsSkeletonDataGrid: React.FC<PerfOrderDetailsSkeletonDataGridProps> = ({
  isMobile = false,
}) => {
  const columnCount = isMobile ? 3 : 4;

  return (
    <div className={`${pagesStandardStyles.dataGrid} ${skeletonStyles.skeletonContainer}`}>
      {/* Header row skeleton */}
      <div className={skeletonStyles.skeletonRow}>
        {Array.from({ length: columnCount }).map((_, index) => (
          <Skeleton
            key={`header-${index}`}
            variant="rectangular"
            height={SKELETON_ROW_HEIGHT}
            sx={{
              flex: 1,
              minWidth: COLUMN_MIN_WIDTH,
              marginRight: index < columnCount - 1 ? '1px' : 0,
              bgcolor: 'grey.200',
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              ...(index === 0 && { borderLeft: 1 }),
            }}
          />
        ))}
      </div>

      {/* 2 data row skeletons - Canceled and Fulfilled */}
      {[0, 1].map(rowIndex => renderRow(rowIndex, columnCount))}
    </div>
  );
};

export default PerfOrderDetailsSkeletonDataGrid;
